import {
    Dialog,
    Box,
    Typography,
    IconButton,
    Divider,
    Button,
    Avatar,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    useMediaQuery,
    Snackbar,
    Tooltip,
    Drawer,
    DialogContent,
} from '@mui/material';
import ddmLoadingGif from '@/assets/images/logo/ddm_loading.gif';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'copy-to-clipboard';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import JSZip from 'jszip';
import { SIZE_TYPE_CONFIG, getSizeGuideImageUrl } from '@/externals/ddm/constants/sizeTypeConfig';
import { washOptions, normalizeWashValue } from '@/externals/ddm/constants/washTypeConfig';
import { Product, WashInfo, getDimEntities } from '@/services/matchingWeb.service';
import { materialValueToLabel } from '@/externals/ddm/constants/materialTypeConfig';
import { MATERIAL_OPTIONS } from '@/constant/materialConfig';
import { extractOptionColor } from '@/externals/ddm/utils/colorUtil';
import { COLOR_PALETTE } from '@/externals/ddm/constants/colorTypeConfig';
import useDDMCategoryStore from '@/externals/ddm/store/categoryStore';
import { useVendorStore } from '@/externals/ddm/store/vendorStore';
import { useLoginFormDialogStore } from '@/externals/ddm/store/loginFormDialogStore';
import sessionStore from '@/store/modules/sessionStore';
import mobile from 'is-mobile';
import { useTranslation } from 'react-i18next';
import { env } from '@/constant/configure';
import { toast } from 'react-toastify';
import {
    getLocalizedDescription,
    getLocalizedGroupTitle,
    getLocalizedNameFromI18n,
    translateCategoryName,
    translateCountryOfOrigin,
    translateWashOptionLabel,
    parseStoreLocation,
} from '@/utils/formatter';
import { getBuildingName, getRegionName } from '@/constant/buildingConfig';

interface SelectedOption {
    quantity: number;
    price: number;
}

interface SelectedOptions {
    [key: string]: SelectedOption;
}

// 공통 스타일 상수
const DETAIL_TYPOGRAPHY_STYLES = {
    label: {
        color: '#222',
        fontWeight: 700,
        fontSize: 14,
    },
    value: {
        fontSize: 14,
    },
} as const;

// material 값을 언어팩 키로 변환하는 함수
const getMaterialKey = (material: string): string => {
    // 먼저 value로 직접 찾기 (이미 영문 키인 경우)
    const foundByValue = MATERIAL_OPTIONS.find((m) => m.value === material);
    if (foundByValue) {
        return foundByValue.value;
    }

    // label로 정확히 찾기 (한글 값인 경우)
    const foundByLabel = MATERIAL_OPTIONS.find((m) => m.label === material);
    if (foundByLabel) {
        return foundByLabel.value;
    }

    // 특수 케이스: "폴리에스터" -> "polyester" (폴리에스테르와 매칭)
    if (material === '폴리에스터' || material.includes('폴리에스터')) {
        return 'polyester';
    }

    // 특수 케이스: "도매처문의" -> "vendor_inquiry"
    if (material === '도매처문의') {
        return 'vendor_inquiry';
    }

    // label로 부분 매칭 찾기 (material이 label의 시작 부분과 일치하는 경우만)
    // 너무 짧은 문자열(1-2자)은 제외하여 오매칭 방지
    const normalizedMaterial = material.trim();
    if (normalizedMaterial.length >= 3) {
        const foundByPartialLabel = MATERIAL_OPTIONS.find((m) => {
            const normalizedLabel = m.label.trim();
            // label이 material로 시작하거나, material이 label로 시작하는 경우
            return normalizedLabel.startsWith(normalizedMaterial) || normalizedMaterial.startsWith(normalizedLabel);
        });
        if (foundByPartialLabel) {
            return foundByPartialLabel.value;
        }
    }

    // 둘 다 아니면 원래 값 반환
    return material;
};

const ProductModal = ({ open, onClose, product }: { open: boolean; onClose: () => void; product: Product | null }) => {
    const { t, i18n } = useTranslation();
    const vendor = product?.vendor;
    const productTitle = useMemo(
        () => getLocalizedGroupTitle(product, i18n.language) || product?.groupTitle || '',
        [product, i18n.language],
    );
    const localizedDescription = useMemo(
        () => getLocalizedDescription(product, i18n.language) || '',
        [product, i18n.language],
    );
    const sanitizedDescription = useMemo(
        () => localizedDescription.replace(/<figure class="image">([\s\S]*?)<\/figure>/g, '$1'),
        [localizedDescription],
    );
    const parsedStoreLocation = useMemo(() => parseStoreLocation(vendor?.vendorBusinessInfo?.storeLocation), [vendor]);
    const localizedStoreLocation = useMemo(() => {
        if (!vendor?.vendorBusinessInfo?.storeLocation) return '';

        const { building, floor, column, unit, rest } = parsedStoreLocation;
        const segments: string[] = [];

        if (building) {
            // 빌딩명에서 지역명 제거 (예: "동대문 디오트" -> "디오트")
            const buildingParts = building.trim().split(/\s+/);
            const regionNames = ['동대문', '남대문', '부산'];
            const buildingNameWithoutRegion = buildingParts
                .filter((part) => !regionNames.includes(part))
                .join(' ')
                .trim();
            const finalBuildingName = buildingNameWithoutRegion || building;
            segments.push(getBuildingName(finalBuildingName));
        }
        if (floor) {
            // "지하1층" -> "B1F" 형식으로 번역
            let translatedFloor = floor;
            // "지하"가 포함된 경우 처리
            if (translatedFloor.includes('지하')) {
                translatedFloor = translatedFloor.replace(/지하/g, t('product.buildingBasement'));
            }
            // "층" 번역
            translatedFloor = translatedFloor.replace(/층/g, t('product.buildingFloor'));
            segments.push(translatedFloor);
        }
        if (column) {
            // "열" 번역 (예: "1열" -> "1Col", "1" -> "1Col")
            let translatedColumn = column;
            // 숫자만 있는 경우 "열" 추가
            if (/^\d+$/.test(translatedColumn)) {
                translatedColumn = `${translatedColumn}열`;
            }
            translatedColumn = translatedColumn.replace(/열/g, t('product.buildingColumn'));
            segments.push(translatedColumn);
        }
        // unit이 없고 storeName이 숫자만 있는 경우, storeName을 호로 처리
        const storeName = vendor?.vendorBusinessInfo?.storeName;
        if (!unit && storeName && /^\d+$/.test(storeName)) {
            // storeName이 숫자만 있으면 호로 처리하고 번역
            const translatedUnit = `${storeName}호`.replace(/호/g, t('product.buildingUnit'));
            segments.push(translatedUnit);
        } else {
            if (unit) {
                // "호" 번역 (예: "123호" -> "123Unit")
                const translatedUnit = unit.replace(/호/g, t('product.buildingUnit'));
                segments.push(translatedUnit);
            }
            if (rest) {
                segments.push(rest);
            }
            // storeName이 숫자가 아니거나 unit이 있는 경우, storeName을 상점명으로 추가
            if (storeName && (!/^\d+$/.test(storeName) || unit)) {
                segments.push(storeName);
            }
        }

        return segments.join(' ').trim();
    }, [vendor, parsedStoreLocation, t, i18n.language]);
    const rmProduct = product?.matchingProducts?.find((p) => p.id === product?.representProductId);
    const productImages = product?.groupImageUrl || [];

    const calculateDiscount = () => {
        if (rmProduct?.discountMethod === 'PERCENT') {
            return rmProduct.discountValue || 0;
        } else {
            const marketPrice = rmProduct?.marketPrice || 0;
            const supplyPrice = rmProduct?.supplyPrice || 0;
            if (marketPrice === 0 || supplyPrice >= marketPrice) return 0;
            return Math.floor(((marketPrice - supplyPrice) / marketPrice) * 100);
        }
    };

    const discount = calculateDiscount();

    // 모든 state와 ref를 최상단에 선언
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expandedAccordions, setExpandedAccordions] = useState({
        detail: true,
        care: true,
        size: true,
        desc: true,
        options: !isMobile,
    });
    const [totalPrice, setTotalPrice] = useState(0);

    useEffect(() => {
        setExpandedAccordions({
            detail: true,
            care: true,
            size: true,
            desc: true,
            options: !isMobile,
        });
        return () => {
            setExpandedAccordions({
                detail: true,
                care: false,
                size: false,
                desc: false,
                options: !isMobile,
            });
            setSelectedOptions({}); // 선택된 옵션 초기화
        };
    }, []);

    const [dimEntities, setDimEntities] = useState<null | any>(null);
    const [dimLoading, setDimLoading] = useState(false);
    const [dimError, setDimError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        setDimLoading(true);
        setDimError(null);
        getDimEntities()
            .then(setDimEntities)
            .catch(() => setDimError('옷감 정보 조회 실패'))
            .finally(() => setDimLoading(false));
    }, [open]);

    const translateDimEntity = useCallback(
        (entity: { name?: string; nameI18n?: Record<string, string> } | null | undefined) =>
            getLocalizedNameFromI18n(entity, i18n.language),
        [i18n.language],
    );

    const translateDimEntityFromCollection = useCallback(
        (
            entity: { id?: string; name?: string; nameI18n?: Record<string, string> } | null | undefined,
            collection: Array<{ id: string; name?: string; nameI18n?: Record<string, string> }> | undefined,
        ) => {
            if (!entity) return '';
            const directTranslation = translateDimEntity(entity);
            if (directTranslation) return directTranslation;

            if (entity.id && Array.isArray(collection)) {
                const match = collection.find((item) => item.id === entity.id);
                if (match) {
                    const translated = translateDimEntity(match);
                    if (translated) return translated;
                }
            }

            return entity.name || '';
        },
        [translateDimEntity],
    );

    const getSeasonName = (ids: string[] = []) => {
        if (!dimEntities?.dimSeason) return '';
        return ids
            .map((id) => {
                const season = dimEntities.dimSeason.find((s: any) => s.id === id);
                return translateDimEntity(season);
            })
            .filter(Boolean)
            .join(', ');
    };
    const getFitName = () => {
        if (!product?.fit) return '';
        const translated = translateDimEntity(product.fit);
        if (translated) return translated;
        if (dimEntities?.dimFit) {
            const match = dimEntities.dimFit.find((f: any) => f.id === product.fit?.id);
            const matchTranslated = translateDimEntity(match);
            if (matchTranslated) return matchTranslated;
            return match?.name || '';
        }
        return product.fit?.name || '';
    };

    const getStyleNames = (ids: string[] = []) => {
        if (!dimEntities?.dimStyle) return [];
        return ids
            .map((id) => {
                const item = dimEntities.dimStyle.find((s: any) => s.id === id);
                return translateDimEntity(item) || item?.name;
            })
            .filter(Boolean);
    };

    const getPatternNames = (ids: string[] = []) => {
        if (!dimEntities?.dimPattern) return [];
        return ids
            .map((id) => {
                const item = dimEntities.dimPattern.find((p: any) => p.id === id);
                return translateDimEntity(item) || item?.name;
            })
            .filter(Boolean);
    };

    const getDetailNames = (ids: string[] = []) => {
        // dimDetail이 없을 수 있으므로 빈 배열 반환
        if (!dimEntities?.dimDetail) return [];
        return ids
            .map((id) => {
                const item = dimEntities.dimDetail.find((d: any) => d.id === id);
                return translateDimEntity(item) || item?.name;
            })
            .filter(Boolean);
    };
    const fabricRows = [
        {
            label: t('product.thickness'),
            value: translateDimEntityFromCollection(product?.thickness, dimEntities?.dimThickness),
        },
        {
            label: t('product.seeThrough'),
            value: translateDimEntityFromCollection(product?.sheerness, dimEntities?.dimSheerness),
        },
        {
            label: t('product.elasticity'),
            value: translateDimEntityFromCollection(product?.stretch, dimEntities?.dimStretch),
        },
        {
            label: t('product.lining'),
            value: translateDimEntityFromCollection(product?.lining, dimEntities?.dimLining),
        },
        {
            label: t('product.season'),
            value: product?.seasonIds && product.seasonIds.length > 0 ? getSeasonName(product.seasonIds) : undefined,
        },
        { label: t('product.fit'), value: getFitName() },
    ].filter((r) => !!r.value);

    const hasFabricData =
        fabricRows.length > 0 ||
        !!product?.thickness ||
        !!product?.sheerness ||
        !!product?.stretch ||
        !!product?.lining ||
        (product?.seasonIds && product.seasonIds.length > 0) ||
        !!product?.fit;

    const shouldRenderFabricInfoBox = dimLoading || !!dimError || hasFabricData;

    const renderFabricInfoBox = () => {
        if (!shouldRenderFabricInfoBox) return null;

        if (dimLoading) {
            return (
                <Box
                    sx={{
                        border: '1px solid #eee',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: 3,
                    }}
                >
                    <Box component="img" src={ddmLoadingGif} alt="loading" sx={{ width: 40, height: 40 }} />
                </Box>
            );
        }

        if (dimError) {
            return (
                <Box
                    sx={{
                        border: '1px solid #eee',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: '#fff5f5',
                    }}
                >
                    <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.value, color: '#d32f2f' }}>{dimError}</Typography>
                </Box>
            );
        }

        // 옵션이 있는 항목들 (테이블 형식 발생)
        const fabricTableRows = [
            // 두께감 추가 (옵션이 있는 경우만)
            ...(dimEntities?.dimThickness && dimEntities.dimThickness.length > 0
                ? [
                      {
                          label: t('product.thickness'),
                          key: 'thickness',
                          options: dimEntities.dimThickness
                              .map((t: any) => translateDimEntity(t) || t.name)
                              .filter(Boolean),
                          selectedValue: translateDimEntityFromCollection(product?.thickness, dimEntities.dimThickness),
                      },
                  ]
                : []),
            // 비침 추가 (옵션이 있는 경우만)
            ...(dimEntities?.dimSheerness && dimEntities.dimSheerness.length > 0
                ? [
                      {
                          label: t('product.seeThrough'),
                          key: 'seeThrough',
                          options: dimEntities.dimSheerness
                              .map((s: any) => translateDimEntity(s) || s.name)
                              .filter(Boolean),
                          selectedValue: translateDimEntityFromCollection(product?.sheerness, dimEntities.dimSheerness),
                      },
                  ]
                : []),
            // 신축성 추가 (옵션이 있는 경우만)
            ...(dimEntities?.dimStretch && dimEntities.dimStretch.length > 0
                ? [
                      {
                          label: t('product.elasticity'),
                          key: 'elasticity',
                          options: dimEntities.dimStretch
                              .map((s: any) => translateDimEntity(s) || s.name)
                              .filter(Boolean),
                          selectedValue: translateDimEntityFromCollection(product?.stretch, dimEntities.dimStretch),
                      },
                  ]
                : []),
            // 안감 추가 (옵션이 있는 경우만)
            ...(dimEntities?.dimLining && dimEntities.dimLining.length > 0
                ? [
                      {
                          label: t('product.lining'),
                          key: 'lining',
                          options: dimEntities.dimLining
                              .map((l: any) => translateDimEntity(l) || l.name)
                              .filter(Boolean),
                          selectedValue: translateDimEntityFromCollection(product?.lining, dimEntities.dimLining),
                      },
                  ]
                : []),
            // 계절감 추가 (옵션이 있는 경우만)
            ...(dimEntities?.dimSeason && dimEntities.dimSeason.length > 0
                ? [
                      {
                          label: t('product.season'),
                          key: 'season',
                          options: dimEntities.dimSeason
                              .map((s: any) => translateDimEntity(s) || s.name)
                              .filter(Boolean),
                          selectedValue:
                              product?.seasonIds && product.seasonIds.length > 0
                                  ? getSeasonName(product.seasonIds)
                                  : undefined,
                      },
                  ]
                : []),
            // 착용감 추가 (옵션이 있는 경우만)
            ...(dimEntities?.dimFit && dimEntities.dimFit.length > 0
                ? [
                      {
                          label: t('product.fit'),
                          key: 'fit',
                          options: dimEntities.dimFit.map((f: any) => translateDimEntity(f) || f.name).filter(Boolean),
                          selectedValue: getFitName(),
                      },
                  ]
                : []),
        ];

        if (!fabricTableRows.length) {
            return (
                <Box
                    sx={{
                        border: '1px solid #eee',
                        borderRadius: 1,
                        p: 2,
                        bgcolor: '#fafafa',
                    }}
                >
                    <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.value }}>{t('product.contactSeller')}</Typography>
                </Box>
            );
        }

        // 최대 옵션 개수 계산
        const maxOptionsCount = Math.max(...fabricTableRows.map((row) => row.options.length));

        return (
            <Box>
                {/* 테이블 형식 항목들 */}
                {fabricTableRows.length > 0 && (
                    <Box
                        sx={{
                            border: 'none',
                            borderRadius: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {fabricTableRows.map((row, index) => (
                            <Box
                                key={row.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'stretch',
                                    borderBottom: index === fabricTableRows.length - 1 ? 'none' : '1px solid #eee',
                                    bgcolor: '#fff',
                                }}
                            >
                                {/* 왼쪽: 카테고리명 */}
                                <Box
                                    sx={{
                                        width: { xs: '30%', md: 140 },
                                        px: 2,
                                        py: 1.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            ...DETAIL_TYPOGRAPHY_STYLES.label,
                                            fontSize: 14,
                                            color: '#222',
                                        }}
                                    >
                                        {row.label}
                                    </Typography>
                                </Box>
                                {/* 오른쪽: 옵션들 */}
                                <Box
                                    sx={{
                                        flex: 1,
                                        px: 2,
                                        py: 1.5,
                                        display: 'grid',
                                        gridTemplateColumns: `repeat(${maxOptionsCount}, 1fr)`,
                                        gap: 3,
                                        alignItems: 'center',
                                    }}
                                >
                                    {Array.from({ length: maxOptionsCount }).map((_, optIndex: number) => {
                                        const option = row.options[optIndex];
                                        if (!option) {
                                            return <Box key={optIndex} />; // 빈 셀
                                        }
                                        const isSelected = option === row.selectedValue;
                                        return (
                                            <Typography
                                                key={optIndex}
                                                sx={{
                                                    fontSize: 14,
                                                    color: isSelected ? '#222' : '#999',
                                                    fontWeight: 400,
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {option}
                                            </Typography>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
                {/* 디자인 특성 섹션 */}
                {((product?.styleIds && product.styleIds.length > 0) ||
                    (product?.patternIds && product.patternIds.length > 0) ||
                    (product?.detailIds && product.detailIds.length > 0)) && (
                    <Box sx={{ mt: 3 }}>
                        <Typography
                            sx={{
                                ...DETAIL_TYPOGRAPHY_STYLES.label,
                                fontSize: 16,
                                mb: 2,
                                fontWeight: 700,
                            }}
                        >
                            {t('product.designCharacteristics')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* 스타일 */}
                            {product?.styleIds && product.styleIds.length > 0 && (
                                <Box>
                                    <Typography
                                        sx={{
                                            ...DETAIL_TYPOGRAPHY_STYLES.label,
                                            fontSize: 14,
                                            mb: 1.5,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t('catalog.style') || '스타일'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {getStyleNames(product.styleIds).map((name: string, index: number) => (
                                            <Box
                                                key={`style-${index}`}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: '#f5f5f5',
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 1,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 13 }}>{name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {/* 패턴 */}
                            {product?.patternIds && product.patternIds.length > 0 && (
                                <Box>
                                    <Typography
                                        sx={{
                                            ...DETAIL_TYPOGRAPHY_STYLES.label,
                                            fontSize: 14,
                                            mb: 1.5,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t('catalog.pattern') || '패턴'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {getPatternNames(product.patternIds).map((name: string, index: number) => (
                                            <Box
                                                key={`pattern-${index}`}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: '#f5f5f5',
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 1,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 13 }}>{name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {/* 디테일 */}
                            {product?.detailIds && product.detailIds.length > 0 && (
                                <Box>
                                    <Typography
                                        sx={{
                                            ...DETAIL_TYPOGRAPHY_STYLES.label,
                                            fontSize: 14,
                                            mb: 1.5,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t('catalog.detail') || '디테일'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {getDetailNames(product.detailIds).map((name: string, index: number) => (
                                            <Box
                                                key={`detail-${index}`}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    bgcolor: '#f5f5f5',
                                                    borderRadius: 1,
                                                    px: 1.5,
                                                    py: 1,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 13 }}>{name}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    };

    // 모달이 닫힐 때 아코디언 상태 초기화
    const handleClose = () => {
        onClose();
    };
    const imageRef = useRef(null);
    const [thumbnailStartIdx, setThumbnailStartIdx] = useState(0);
    const thumbnailRef = useRef<HTMLDivElement>(null);

    // 썸네일 네비게이션 상태
    const visibleThumbnails = isMobile ? 4 : 6;
    const [thumbnailStartIndex, setThumbnailStartIndex] = useState(0);
    const [thumbnailTouchStart, setThumbnailTouchStart] = useState(0);
    const [thumbnailTouchEnd, setThumbnailTouchEnd] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(0);
    const [scrollStart, setScrollStart] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [isOptionsDrawerOpen, setIsOptionsDrawerOpen] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
    const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
    const [showScrollTop, setShowScrollTop] = useState(false);

    // 반응형 크기 설정
    const imgBoxWidth = isMobile ? '100vw' : 530;
    const imgBoxHeight = isMobile ? 707 : 707; // 모바일에서도 동일한 높이 사용
    const imageAspectRatio = '530/707'; // PC 이미지 비율 (530:707)

    // 이미지 배열 정보
    const totalImages = productImages.length;
    const hasMultipleImages = totalImages > 1;

    // 이미지 인덱스 초기화
    useEffect(() => {
        if (open) {
            setCurrentIndex(0);
            setThumbnailStartIdx(0);
            setThumbnailStartIndex(0);
            setIsOptionsDrawerOpen(false);
        }
        return () => {
            setSelectedOptions({});
            setTotalPrice(0);
            setDownloadProgress(0);
            setIsDownloading(false);
            setLoadedImages(new Set());
        };
    }, [open]);

    // 이벤트 핸들러 함수들
    const handleAccordion = (panel: string) => (_event: any, isExpanded: boolean) => {
        setExpandedAccordions((prev) => ({
            ...prev,
            [panel]: isExpanded,
        }));
    };

    // 다음 슬라이드로 이동
    const goToNext = useCallback(() => {
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => (prev < totalImages - 1 ? prev + 1 : prev));
    }, [hasMultipleImages, totalImages]);

    // 이전 슬라이드로 이동
    const goToPrev = useCallback(() => {
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
    }, [hasMultipleImages]);

    // 터치 이벤트 핸들러
    const handleTouchStart = useCallback((e: React.TouchEvent | React.MouseEvent) => {
        const clientX =
            e.type === 'mousedown' ? (e as React.MouseEvent).clientX : (e as React.TouchEvent).touches[0].clientX;
        setTouchStart(clientX);
        setTouchEnd(clientX);
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent | React.MouseEvent) => {
        const clientX =
            e.type === 'mousemove' ? (e as React.MouseEvent).clientX : (e as React.TouchEvent).touches[0].clientX;
        setTouchEnd(clientX);
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrev();
        }
    }, [touchStart, touchEnd, goToNext, goToPrev]);

    // 썸네일 네비게이션 함수들
    const goToPrevThumbnails = useCallback(() => {
        setThumbnailStartIndex((prev) => Math.max(0, prev - visibleThumbnails));
    }, [visibleThumbnails]);

    const goToNextThumbnails = useCallback(() => {
        setThumbnailStartIndex((prev) => Math.min(productImages.length - visibleThumbnails, prev + visibleThumbnails));
    }, [productImages.length, visibleThumbnails]);

    // 활성 썸네일이 보이도록 자동 스크롤
    const scrollToActiveThumbnail = useCallback(() => {
        const activeIndex = currentIndex;
        const startIndex = thumbnailStartIndex;
        const endIndex = startIndex + visibleThumbnails - 1;

        if (activeIndex < startIndex) {
            // 활성 썸네일이 왼쪽에 숨겨진 경우
            setThumbnailStartIndex(Math.max(0, activeIndex - Math.floor(visibleThumbnails / 2)));
        } else if (activeIndex > endIndex) {
            // 활성 썸네일이 오른쪽에 숨겨진 경우
            setThumbnailStartIndex(
                Math.min(productImages.length - visibleThumbnails, activeIndex - Math.floor(visibleThumbnails / 2)),
            );
        }
    }, [currentIndex, thumbnailStartIndex, visibleThumbnails, productImages.length]);

    // 현재 이미지 변경 시 썸네일 스크롤 조정
    useEffect(() => {
        scrollToActiveThumbnail();
    }, [currentIndex, scrollToActiveThumbnail]);

    // 썸네일 터치 이벤트 핸들러
    const handleThumbnailTouchStart = useCallback((e: React.TouchEvent) => {
        setThumbnailTouchStart(e.touches[0].clientX);
        setThumbnailTouchEnd(e.touches[0].clientX);
    }, []);

    const handleThumbnailTouchMove = useCallback((e: React.TouchEvent) => {
        setThumbnailTouchEnd(e.touches[0].clientX);
    }, []);

    const handleThumbnailTouchEnd = useCallback(() => {
        if (!thumbnailTouchStart || !thumbnailTouchEnd) return;

        const distance = thumbnailTouchStart - thumbnailTouchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && thumbnailStartIndex + visibleThumbnails < productImages.length) {
            goToNextThumbnails();
        } else if (isRightSwipe && thumbnailStartIndex > 0) {
            goToPrevThumbnails();
        }
    }, [
        thumbnailTouchStart,
        thumbnailTouchEnd,
        thumbnailStartIndex,
        visibleThumbnails,
        productImages.length,
        goToNextThumbnails,
        goToPrevThumbnails,
    ]);

    // 마우스 드래그 핸들러
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        setDragStart(e.clientX);
        setScrollStart(thumbnailRef.current?.scrollLeft || 0);
        e.preventDefault();
    }, []);

    const handleMouseMove = useCallback(
        (e: React.MouseEvent) => {
            if (!isDragging || !thumbnailRef.current) return;

            const deltaX = e.clientX - dragStart;
            thumbnailRef.current.scrollLeft = scrollStart - deltaX;
            e.preventDefault();
        },
        [isDragging, dragStart, scrollStart],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsDragging(false);
    }, []);

    // 키보드 네비게이션
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!hasMultipleImages) return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    goToPrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasMultipleImages, goToPrev, goToNext]);

    const handleDownloadImages = async () => {
        const { hasSession } = sessionStore.getState();
        if (!hasSession) {
            setIsOpen(true);
            toast.info(t('product.loginRequiredForDownload'), { toastId: 'require-login-download' });
            return;
        }

        try {
            setIsDownloading(true);
            setDownloadProgress(0);
            const totalImages = productImages.length;

            if (!mobile()) {
                for (const [index, imageUrl] of productImages.entries()) {
                    try {
                        const response = await fetch(imageUrl);
                        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                        link.href = url;
                        link.download = `image_${index + 1}_${timestamp}.jpg`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);

                        setDownloadProgress(((index + 1) / totalImages) * 100);
                    } catch (error) {
                        console.error(`이미지 ${index + 1} 다운로드 실패:`, error);
                    }
                }
                setSnackbarMessage(t('product.imageDownloadComplete'));
                setSnackbarOpen(true);
            } else {
                const zip = new JSZip();
                let successCount = 0;

                for (const [index, imageUrl] of productImages.entries()) {
                    try {
                        const response = await fetch(imageUrl);
                        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                        const blob = await response.blob();
                        zip.file(`image_${index + 1}.jpg`, blob);

                        successCount++;
                        setDownloadProgress((successCount / totalImages) * 100);
                    } catch (error) {
                        console.error(`이미지 ${index + 1} 다운로드 실패:`, error);
                    }
                }

                if (successCount > 0) {
                    setDownloadProgress(100);

                    const content = await zip.generateAsync({
                        type: 'blob',
                        compression: 'DEFLATE',
                        compressionOptions: {
                            level: 9,
                        },
                    });

                    const url = window.URL.createObjectURL(content);
                    const link = document.createElement('a');
                    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
                    link.href = url;
                    link.download = `images_${timestamp}.zip`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    setSnackbarMessage(t('product.imageDownloadComplete'));
                    setSnackbarOpen(true);
                } else {
                    setSnackbarMessage(t('product.imageDownloadFailed'));
                    setSnackbarOpen(true);
                }
            }
        } catch (error) {
            console.error('이미지 다운로드 중 오류 발생:', error);
            setSnackbarMessage(t('product.imageDownloadError'));
            setSnackbarOpen(true);
        } finally {
            setIsDownloading(false);
            setDownloadProgress(0);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    // API 응답 예시 (실제로는 product prop으로 받아올 예정)

    interface WashOption {
        value: string;
        label: string;
        icon: string;
    }

    const renderWashInfo = () => {
        if (!product?.washInfo) return null;

        const allSelectedOptions: (WashOption & { category: string })[] = [];
        const addedValues = new Set<string>(); // 중복 제거를 위한 Set

        // etc 배열 처리
        const etcValues = product.washInfo.etc;
        if (etcValues && Array.isArray(etcValues) && etcValues.length > 0) {
            etcValues.forEach((etcValue: string) => {
                // 값 정규화
                const normalized = normalizeWashValue(etcValue);

                if (normalized) {
                    // 중복 체크
                    if (addedValues.has(normalized.value)) return;

                    // 정규화된 카테고리에서 찾기
                    const categoryOptions = washOptions[normalized.category] as WashOption[];
                    const matchingOption = categoryOptions.find((option) => option.value === normalized.value);
                    if (matchingOption) {
                        allSelectedOptions.push({
                            ...matchingOption,
                            category: normalized.category,
                        });
                        addedValues.add(normalized.value);
                    }
                }
            });
        }

        // 다른 필드들 처리 (문자열 값들)
        const stringFields = ['dry', 'iron', 'bleach', 'method', 'special', 'detergent'] as const;
        stringFields.forEach((field) => {
            const value = product.washInfo?.[field];
            if (value && typeof value === 'string') {
                // 값 정규화
                const normalized = normalizeWashValue(value);
                const searchValue = normalized ? normalized.value : value;

                // 중복 체크
                if (addedValues.has(searchValue)) return;

                const categoryOptions = washOptions[field] as WashOption[];
                const matchingOption = categoryOptions.find((option) => option.value === searchValue);
                if (matchingOption) {
                    allSelectedOptions.push({
                        ...matchingOption,
                        category: field,
                    });
                    addedValues.add(searchValue);
                }
            }
        });

        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {allSelectedOptions.map((option) => {
                    const translatedLabel =
                        translateWashOptionLabel(option.category, option.value, t, option.label) || option.label;

                    return (
                        <Tooltip key={`${option.category}-${option.value}`} title={translatedLabel} placement="top">
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    bgcolor: '#f5f5f5',
                                    borderRadius: 1,
                                    px: 1.5,
                                    py: 1,
                                }}
                            >
                                <img
                                    src={option.icon}
                                    alt={translatedLabel}
                                    style={{
                                        width: 20,
                                        height: 20,
                                        objectFit: 'contain',
                                    }}
                                />
                                <Typography sx={{ fontSize: 13 }}>{translatedLabel}</Typography>
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        );
    };

    // 이미지 로딩 처리만 남김
    useEffect(() => {
        if (!localizedDescription) return;

        const handleImageLoad = (event: Event) => {
            const img = event.target as HTMLImageElement;
            setLoadedImages((prev) => new Set([...prev, img.src]));
        };

        const images = document.querySelectorAll('.product-description img');
        images.forEach((img) => {
            const imgElement = img as HTMLImageElement;

            if (imgElement.complete && imgElement.naturalWidth > 0) {
                setLoadedImages((prev) => new Set([...prev, imgElement.src]));
            } else {
                imgElement.addEventListener('load', handleImageLoad);
            }
        });

        return () => {
            images.forEach((img) => {
                img.removeEventListener('load', handleImageLoad);
            });
        };
    }, [localizedDescription]);

    const handleOptionsDrawerOpen = () => {
        setIsOptionsDrawerOpen(true);
    };

    const handleOptionsDrawerClose = () => {
        setIsOptionsDrawerOpen(false);
    };

    const handleLoginClick = () => {
        setIsOpen(true);
    };

    const handleVendorProfileClick = () => {
        if (vendor?.vendorPath) {
            // 로그인된 사용자의 경우 토큰을 URL 파라미터로 전달
            const { accessToken } = sessionStore.getState();
            const tokenParam = accessToken ? `?token=${encodeURIComponent(accessToken)}` : '';

            // vendorPath에서 실제 경로만 추출 (예: https://vendor.dev.flowing.space/xicata961 -> xicata961)
            const vendorPath = vendor.vendorPath.split('/').pop() || '';

            const domain = env !== 'prd' ? 'dev.shop3.io' : 'shop3.io';
            window.open(`https://${domain}/${vendorPath}${tokenParam}`, '_blank');
        }
    };

    const handleCopyProductId = () => {
        if (product?.id) {
            const result = copy(product.id);
            if (result) {
                toast.success('상품 ID가 복사되었습니다');
            } else {
                toast.error('복사에 실패했습니다');
            }
        }
    };

    const handleQuantityChange = (id: string, change: number) => {
        const option = product?.matchingProducts?.find((p) => p.id === id);
        setSelectedOptions((prev) => {
            const currentQty = prev[id]?.quantity || 0;
            const newQty = Math.max(0, currentQty + change);

            if (newQty === 0) {
                const newOptions = { ...prev };
                delete newOptions[id];
                return newOptions;
            }

            return {
                ...prev,
                [id]: {
                    quantity: newQty,
                    price: option?.supplyPrice || 0,
                },
            };
        });
    };

    useEffect(() => {
        const _totalPrice = Object.values(selectedOptions).reduce((total: number, option: SelectedOption) => {
            return total + option.quantity * option.price;
        }, 0);
        setTotalPrice(_totalPrice);
    }, [selectedOptions]);

    // 옵션 정보 추출 함수
    const extractOptionValues = (optionType: 'color' | 'size') => {
        if (!product?.optionShowOrder || !product?.productInfo) {
            return '';
        }

        const uniqueValues = new Set<string>();

        // 키워드 정의 (모두 소문자로)
        const keywords = {
            color: ['color', t('product.color'), t('product.color')],
            size: ['size', t('product.size')],
        };

        product.productInfo.forEach((info) => {
            info.options
                .filter((opt) => {
                    // optionKey 또는 option_key 처리 (소문자로 변환)
                    const key = (opt.optionKey || opt.option_key || '').toLowerCase();
                    const targetKeywords = keywords[optionType];

                    // 소문자로 변환된 키와 비교
                    return targetKeywords.some((keyword) => key === keyword);
                })
                .forEach((opt) => {
                    // optionValue 또는 option_value 처리
                    const value = opt?.optionValue || opt?.option_value;
                    if (value) {
                        if (optionType === 'color' && value?.startsWith('#')) {
                            // colorNameInfo에서 먼저 찾기
                            const customName = product?.colorNameInfo?.[value];
                            if (customName) {
                                uniqueValues.add(customName);
                            } else {
                                // COLOR_PALETTE에서 찾기
                                const colorInfo = COLOR_PALETTE?.find(
                                    (c) => c.value?.toLowerCase() === value?.toLowerCase(),
                                );
                                uniqueValues.add(colorInfo ? colorInfo?.name : value);
                            }
                        } else {
                            uniqueValues.add(value);
                        }
                    }
                });
        });

        // 정렬된 배열로 변환
        const sortedValues = Array.from(uniqueValues).sort((a, b) => {
            // 사이즈의 경우 숫자 기준으로 정렬
            if (optionType === 'size') {
                const getNumber = (str: string) => parseInt(str.match(/\d+/)?.[0] || '0');
                return getNumber(a) - getNumber(b);
            }
            return a.localeCompare(b);
        });

        return sortedValues.join(', ');
    };

    // 옵션 정보 추출
    const colorInfo = extractOptionValues('color');
    const sizeInfo = extractOptionValues('size');

    const getCategoryById = useDDMCategoryStore((state) => state.getCategoryById);
    const { setIsOpen } = useLoginFormDialogStore((state) => state.action);

    // product가 있을 때만 카테고리 정보 조회
    const category = product ? getCategoryById(product.categoryId) : undefined;

    // 세탁 및 상품 주의사항 표시 여부를 확인하는 함수
    const hasWashInfo = Boolean(
        product?.washInfo && Object.values(product.washInfo).some((values) => values && values.length > 0),
    );
    const shouldShowCareInfo = () => shouldRenderFabricInfoBox || hasWashInfo;

    // 제품설명 썸네일(대표/그룹 이미지) 렌더링 유틸
    const getDetailThumbnailImages = (): string[] => {
        const images: string[] = [];
        if (product?.representImageUrl) images.push(product.representImageUrl);
        if (product && Array.isArray(product.groupImageUrl)) images.push(...product.groupImageUrl);
        return Array.from(new Set(images.filter(Boolean)));
    };

    const shouldRenderDetailThumbnails = product?.useThumbnailInDetail === 't';
    const isThumbPositionTop = product?.thumbnailInDetailPosition === 'top';

    const renderDetailThumbnails = () => {
        const images = getDetailThumbnailImages();
        if (!images.length) return null;
        return (
            <Box className="product-description">
                {images.map((img, idx) => (
                    <img key={`detail-thumb-${idx}`} src={img} alt={`thumbnail-${idx}`} loading="lazy" />
                ))}
            </Box>
        );
    };

    // 조건부 렌더링
    if (!product || !open) return null;

    const currentSizeConfig = SIZE_TYPE_CONFIG.find((config) => config.value === product.sizeType);

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xl"
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', md: '1200px' },
                        height: { xs: '100%', md: '95vh' },
                        maxHeight: { xs: '100%', md: '95vh' },
                        minHeight: { xs: '100%', md: '600px' },
                    },
                }}
            >
                <DialogContent
                    onScroll={(e) => {
                        const top = e.currentTarget.scrollTop || 0;
                        if (top > 300) {
                            setShowScrollTop(true);
                        } else {
                            setShowScrollTop(false);
                        }
                    }}
                    id="#modalRef"
                    sx={{
                        padding: 0,
                        height: '100%',
                        overflow: 'auto',
                    }}
                >
                    {/* 상단 카테고리/경로 - PC에서만 표시 */}
                    {!isMobile && (
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                                bgcolor: '#fff',
                                pt: 4,
                                px: 4,
                                pb: 2,
                                borderBottom: '1px solid #eee',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: '#888',
                                        fontWeight: 600,
                                    }}
                                >
                                    {translateCategoryName(category?.wholeCategoryName?.split('>')[0]?.trim() || '', t)}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: '#888' }}>&gt;</Typography>
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: '#222',
                                        fontWeight: 600,
                                    }}
                                >
                                    {translateCategoryName(category?.wholeCategoryName?.split('>')[1]?.trim() || '', t)}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    position: 'absolute',
                                    top: { md: 20 },
                                    right: { md: 32 },
                                    width: 32,
                                    height: 32,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    )}

                    {/* 메인 컨텐츠 영역 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 0, md: 4 },
                        }}
                    >
                        {/* 상품 정보 컨테이너 */}
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                gap: { xs: 2, md: 5.5 },
                                mb: { xs: 1, md: 4 },
                            }}
                        >
                            {/* 모바일 뒤로가기 버튼 */}
                            {isMobile && (
                                <IconButton
                                    onClick={handleClose}
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        top: 16,
                                        width: 40,
                                        height: 40,
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        zIndex: 10,
                                        '&:hover': {
                                            bgcolor: 'rgba(255,255,255,1)',
                                        },
                                    }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                            )}

                            {/* 좌측: 이미지/슬라이더 */}
                            <Box
                                sx={{
                                    width: { xs: '100%', md: imgBoxWidth },
                                    minWidth: { xs: '100%', md: imgBoxWidth },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                    position: 'relative',
                                }}
                            >
                                {/* 이미지 슬라이더 */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: { xs: 'auto', md: imgBoxHeight },
                                        aspectRatio: imageAspectRatio,
                                        mb: 2,
                                        bgcolor: '#fff',
                                        borderRadius: { xs: 0, md: 2 },
                                        overflow: 'hidden',
                                        cursor: 'default',
                                    }}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    onMouseDown={handleTouchStart}
                                    onMouseMove={handleTouchMove}
                                    onMouseUp={handleTouchEnd}
                                    onMouseLeave={handleTouchEnd}
                                >
                                    {/* 이미지 컨테이너 */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            width: hasMultipleImages ? `${totalImages * 100}%` : '100%',
                                            height: '100%',
                                            transform: hasMultipleImages
                                                ? `translateX(-${(currentIndex * 100) / totalImages}%)`
                                                : 'translateX(0%)',
                                            transition: 'transform 0.2s ease-in-out',
                                            willChange: 'transform',
                                        }}
                                    >
                                        {productImages.map((image, index) => (
                                            <Box
                                                key={`${image}-${index}`}
                                                sx={{
                                                    width: hasMultipleImages ? `${100 / totalImages}%` : '100%',
                                                    height: '100%',
                                                    position: 'relative',
                                                }}
                                            >
                                                <img
                                                    src={image}
                                                    alt={productTitle}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                        userSelect: 'none',
                                                        WebkitUserSelect: 'none',
                                                        pointerEvents: 'none',
                                                    }}
                                                    draggable={false}
                                                    loading="lazy"
                                                />
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* 좌우 화살표 - PC에서만 표시 */}
                                    {!isMobile && hasMultipleImages && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                px: 2,
                                                '&:hover': {
                                                    opacity: 1,
                                                },
                                            }}
                                        >
                                            <IconButton
                                                onClick={goToPrev}
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.8)',
                                                    borderRadius: 1.5,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255,255,255,0.9)',
                                                    },
                                                }}
                                            >
                                                <ChevronLeftIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={goToNext}
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.8)',
                                                    borderRadius: 1.5,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255,255,255,0.9)',
                                                    },
                                                }}
                                            >
                                                <ChevronRightIcon />
                                            </IconButton>
                                        </Box>
                                    )}
                                    {/* 인디케이터 */}
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            right: 10,
                                            bottom: 10,
                                            bgcolor: 'rgba(0,0,0,0.6)',
                                            color: '#fff',
                                            borderRadius: 1,
                                            px: 1.2,
                                            py: 0.5,
                                            fontSize: 13,
                                        }}
                                    >
                                        {hasMultipleImages
                                            ? `${currentIndex + 1} / ${totalImages}`
                                            : `1 / ${totalImages}`}
                                    </Box>
                                </Box>

                                {/* 다운로드 버튼 */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mb: 2,
                                        px: { xs: 2, md: 0 },
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={
                                            isDownloading ? (
                                                <img
                                                    src={ddmLoadingGif}
                                                    alt="Loading..."
                                                    style={{ width: '20px', height: '20px' }}
                                                />
                                            ) : (
                                                <DownloadIcon />
                                            )
                                        }
                                        onClick={handleDownloadImages}
                                        disabled={isDownloading}
                                        sx={{
                                            borderColor: '#222',
                                            color: '#222',
                                            height: 48,
                                            borderRadius: 1.5,
                                            position: 'relative',
                                            '&:hover': {
                                                borderColor: '#000',
                                                backgroundColor: 'rgba(0,0,0,0.04)',
                                            },
                                        }}
                                    >
                                        {isDownloading ? (
                                            <>
                                                {t('product.downloading')} {Math.round(downloadProgress)}%
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        width: `${downloadProgress}%`,
                                                        height: '2px',
                                                        bgcolor: '#222',
                                                        transition: 'width 0.3s ease',
                                                    }}
                                                />
                                            </>
                                        ) : (
                                            t('product.downloadImages')
                                        )}
                                    </Button>
                                </Box>

                                {/* 썸네일 리스트 */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        overflow: 'hidden',
                                        mb: { xs: 2, md: 0 },
                                        px: { xs: 2, md: 0 },
                                    }}
                                >
                                    <Box
                                        ref={thumbnailRef}
                                        onMouseDown={handleMouseDown}
                                        onMouseMove={handleMouseMove}
                                        onMouseUp={handleMouseUp}
                                        onMouseLeave={handleMouseLeave}
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            width: '100%',
                                            position: 'relative',
                                            cursor: isDragging ? 'grabbing' : 'grab',
                                            overflowX: 'auto',
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            '&::-webkit-scrollbar': {
                                                display: 'none',
                                            },
                                        }}
                                    >
                                        {productImages.map((img, idx) => {
                                            const isActive = idx === currentIndex;

                                            return (
                                                <Box
                                                    key={idx}
                                                    sx={{
                                                        position: 'relative',
                                                        border: isActive ? '3px solid #222' : '1px solid #eee',
                                                        borderRadius: 2,
                                                        cursor: 'pointer',
                                                        width: `calc((100% - 5px * 5) / 6)`,
                                                        minWidth: `calc((100% - 5px * 5) / 6)`,
                                                        aspectRatio: '1/1',
                                                        overflow: 'hidden',
                                                        flexShrink: 0,
                                                    }}
                                                    onClick={() => {
                                                        if (hasMultipleImages) {
                                                            setCurrentIndex(idx);
                                                        }
                                                    }}
                                                >
                                                    <img
                                                        src={img}
                                                        alt={productTitle}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                            userSelect: 'none',
                                                        }}
                                                        draggable={false}
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Box>

                            {/* 우측: 상품 정보 영역 */}
                            <Box
                                sx={{
                                    flex: 1,
                                    // minWidth: { xs: 0, md: 500 },
                                    display: 'flex',
                                    flexDirection: 'column',
                                    px: { xs: 2, md: 0 },
                                    flexShrink: 1,
                                }}
                            >
                                {/* 모바일에서만 보이는 카테고리 */}
                                {isMobile && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: '#888',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {translateCategoryName(
                                                category?.wholeCategoryName?.split('>')[0]?.trim() || '',
                                                t,
                                            )}
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#888' }}>&gt;</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 13,
                                                color: '#222',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {translateCategoryName(
                                                category?.wholeCategoryName?.split('>')[1]?.trim() || '',
                                                t,
                                            )}
                                        </Typography>
                                    </Box>
                                )}

                                {/* PC에서만 보이는 판매자 정보 */}
                                {!isMobile && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 3,
                                            cursor: 'pointer',
                                            '&:hover': {
                                                opacity: 0.8,
                                            },
                                        }}
                                        onClick={handleVendorProfileClick}
                                    >
                                        <Avatar src={vendor?.imageUrl || ''} sx={{ width: 44, height: 44, mr: 2 }} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                                                {vendor?.nickname || ''}
                                            </Typography>
                                            <Typography sx={{ fontSize: 13, color: '#888' }}>
                                                {vendor?.vendorBusinessInfo?.storeLocation &&
                                                vendor?.vendorBusinessInfo?.storeName ? (
                                                    <>{localizedStoreLocation}</>
                                                ) : (
                                                    <>{t('product.storeLocationContactSeller')}</>
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                {/* 상품명/가격/아이콘 */}
                                <Typography
                                    variant="h1"
                                    sx={{ fontSize: 24, fontWeight: 700, lineHeight: '34px', mb: 1 }}
                                >
                                    {productTitle}
                                </Typography>
                                {/* 상품 ID 복사 */}
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        mb: 2,
                                        cursor: 'pointer',
                                        '&:hover': { opacity: 0.7 },
                                        transition: 'opacity 0.2s',
                                    }}
                                    onClick={handleCopyProductId}
                                >
                                    <Typography sx={{ fontSize: 12, color: '#888' }}>
                                        ID: {product?.id}
                                    </Typography>
                                    <ContentCopyIcon sx={{ fontSize: 14, color: '#888' }} />
                                </Box>
                                <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
                                    {sessionStore.getState().hasSession ? (
                                        <>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                {/* <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#f00' }}>
                                                    {discount}%
                                                </Typography>
                                                <Typography
                                                    sx={{ fontSize: 16, color: '#999', textDecoration: 'line-through' }}
                                                >
                                                    ₩
                                                    {(rmProduct?.marketPrice
                                                        ? rmProduct.marketPrice
                                                        : 0
                                                    )?.toLocaleString()}
                                                </Typography> */}
                                            </Box>
                                            <Typography sx={{ fontSize: 28, fontWeight: 700 }}>
                                                ₩{rmProduct?.supplyPrice?.toLocaleString()}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography sx={{ fontSize: 16, color: '#666', fontWeight: 500 }}>
                                            {t('product.priceForMembers')}
                                        </Typography>
                                    )}
                                </Box>

                                {/* 모바일에서만 보이는 판매자 정보 */}
                                {isMobile && (
                                    <>
                                        <Divider sx={{ borderColor: '#eee', my: { xs: 1, md: 3 } }} />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: { xs: 2, md: 3 },
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    opacity: 0.8,
                                                },
                                            }}
                                            onClick={handleVendorProfileClick}
                                        >
                                            <Avatar
                                                src={product.vendor?.imageUrl || ''}
                                                sx={{ width: 32, height: 32, mr: 2 }}
                                            />
                                            <Box>
                                                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                                    {product.vendor?.nickname || ''}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#888' }}>
                                                    {vendor?.vendorBusinessInfo?.storeLocation &&
                                                    vendor?.vendorBusinessInfo?.storeName ? (
                                                        <>{localizedStoreLocation}</>
                                                    ) : (
                                                        <>{t('product.storeLocationContactSeller')}</>
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}

                                {/* 구분선 추가 */}
                                <Divider sx={{ borderColor: '#000', borderBottomWidth: 2 }} />

                                {/* 상세정보 아코디언 */}
                                {!isMobile && (
                                    <Accordion
                                        expanded={expandedAccordions.detail}
                                        onChange={handleAccordion('detail')}
                                        sx={{ boxShadow: 'none', mb: 1 }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                        >
                                            {t('product.detailInfo')}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: { xs: '80px 1fr', md: '120px 1fr' },
                                                    rowGap: 1,
                                                    columnGap: 2,
                                                }}
                                            >
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                                    {t('catalog.manufacturingCountry')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                    {product?.countryOfOrigin
                                                        ? translateCountryOfOrigin(product.countryOfOrigin, t)
                                                        : '-'}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                                    {t('product.color')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                    {colorInfo || '-'}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                                    {t('product.size')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                    {sizeInfo || '-'}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                                    {t('product.mixingRatio')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                    {product?.materialInfo && product.materialInfo.length > 0
                                                        ? product.materialInfo
                                                              .map((m) => {
                                                                  const materialKey = getMaterialKey(m.material);
                                                                  const translatedMaterial = t(
                                                                      `catalog.materials.${materialKey}`,
                                                                  );
                                                                  const materialLabel =
                                                                      translatedMaterial ===
                                                                      `catalog.materials.${materialKey}`
                                                                          ? m.material
                                                                          : translatedMaterial;
                                                                  // 도매처문의일 때는 percentage 표시하지 않음
                                                                  if (
                                                                      materialKey === 'vendor_inquiry' ||
                                                                      m.material === '도매처문의'
                                                                  ) {
                                                                      return materialLabel;
                                                                  }
                                                                  return `${materialLabel} ${m.percentage}%`;
                                                              })
                                                              .join(', ')
                                                        : t('product.contactSeller')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                                    {t('product.individualSheet')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                    {(product as any)?.singleUnitAvailable === 't'
                                                        ? t('product.available')
                                                        : (product as any)?.singleUnitAvailable === 'f'
                                                          ? t('product.unavailable')
                                                          : t('product.contactSeller')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                                    {t('product.productRegistrationInfo')}
                                                </Typography>
                                                <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                                    {product?.createdAt?.split('T')[0]} {t('product.registered')}
                                                </Typography>
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* 구분선 추가 */}
                                <Divider sx={{ borderColor: '#eee' }} />

                                {/* 세탁 및 상품 주의사항 아코디언 */}
                                {!isMobile && shouldShowCareInfo() && (
                                    <Accordion
                                        expanded={expandedAccordions.care}
                                        onChange={handleAccordion('care')}
                                        sx={{ boxShadow: 'none', mb: 1 }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                        >
                                            {t('product.careInstructions')}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ mb: 1, fontSize: { xs: 13, md: 15 } }}>
                                                {shouldRenderFabricInfoBox && (
                                                    <Box sx={{ mb: hasWashInfo ? 3 : 0 }}>
                                                        <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                            {t('product.fabricInfo')}
                                                        </Typography>
                                                        {renderFabricInfoBox()}
                                                    </Box>
                                                )}
                                                {hasWashInfo && (
                                                    <>
                                                        <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                            {t('product.washInfo')}
                                                        </Typography>
                                                        {renderWashInfo()}
                                                    </>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* 구분선 추가 */}
                                <Divider sx={{ borderColor: '#eee' }} />

                                {/* 상품 옵션 아코디언 */}
                                {!isMobile && (
                                    <Accordion
                                        expanded={expandedAccordions.options}
                                        onChange={handleAccordion('options')}
                                        sx={{ boxShadow: 'none', mb: 1 }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                        >
                                            {t('product.productOptions')}
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                {/* 옵션 목록 */}
                                                {product?.matchingProducts
                                                    ?.filter((p) => p?.isActivated !== 'f' && p?.isPublic !== 'f')
                                                    .map((option) => {
                                                        const newOption = extractOptionColor(
                                                            option,
                                                            product.colorNameInfo,
                                                        );
                                                        return (
                                                            <Box
                                                                key={'optionList_' + option.id}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    pb: 2,
                                                                    borderBottom: '1px solid #eee',
                                                                    '&:last-child': {
                                                                        borderBottom: 'none',
                                                                        pb: 0,
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1.5,
                                                                    }}
                                                                >
                                                                    {(newOption?.optionImageUrl ||
                                                                        newOption?.color) && (
                                                                        <Box
                                                                            sx={{
                                                                                width: 48,
                                                                                height: 48,
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                border: '1px solid #eee',
                                                                            }}
                                                                        >
                                                                            {newOption?.optionImageUrl ? (
                                                                                <img
                                                                                    src={newOption?.optionImageUrl}
                                                                                    alt={
                                                                                        getLocalizedNameFromI18n(
                                                                                            {
                                                                                                name: newOption?.optionName,
                                                                                                nameI18n: (
                                                                                                    option as any
                                                                                                ).optionNameI18n,
                                                                                            },
                                                                                            i18n.language,
                                                                                        ) || newOption?.optionName
                                                                                    }
                                                                                    style={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        objectFit: 'cover',
                                                                                    }}
                                                                                />
                                                                            ) : (
                                                                                <Box
                                                                                    sx={{
                                                                                        width: '100%',
                                                                                        height: '100%',
                                                                                        bgcolor: newOption?.color,
                                                                                        border: '1px solid #eee',
                                                                                        borderRadius: 1,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                    <Box>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 14,
                                                                                ...(!newOption?.optionImageUrl &&
                                                                                !newOption?.color
                                                                                    ? {
                                                                                          display: 'inline-block',
                                                                                          mr: 1,
                                                                                      }
                                                                                    : {
                                                                                          mb: 0.2,
                                                                                      }),
                                                                            }}
                                                                        >
                                                                            {getLocalizedNameFromI18n(
                                                                                {
                                                                                    name: newOption.optionName,
                                                                                    nameI18n: (option as any)
                                                                                        .optionNameI18n,
                                                                                },
                                                                                i18n.language,
                                                                            ) || newOption.optionName}
                                                                        </Typography>
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 14,
                                                                                fontWeight: 700,
                                                                                ...(!newOption?.optionImageUrl &&
                                                                                !newOption?.color
                                                                                    ? {
                                                                                          display: 'inline-block',
                                                                                      }
                                                                                    : {}),
                                                                            }}
                                                                        >
                                                                            {sessionStore.getState().hasSession ? (
                                                                                <>
                                                                                    ₩
                                                                                    {newOption?.supplyPrice?.toLocaleString()}
                                                                                </>
                                                                            ) : (
                                                                                <></>
                                                                            )}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1.5,
                                                                    }}
                                                                >
                                                                    {sessionStore.getState().hasSession ? (
                                                                        <Box
                                                                            sx={{
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                border: '1px solid #ddd',
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                            }}
                                                                        >
                                                                            <Button
                                                                                onClick={() =>
                                                                                    handleQuantityChange(
                                                                                        newOption?.id,
                                                                                        -1,
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    minWidth: 32,
                                                                                    height: 32,
                                                                                    borderRadius: 0,
                                                                                    borderRight: '1px solid #ddd',
                                                                                    color: '#666',
                                                                                }}
                                                                            >
                                                                                -
                                                                            </Button>
                                                                            <Typography
                                                                                sx={{
                                                                                    width: 40,
                                                                                    textAlign: 'center',
                                                                                    fontSize: 13,
                                                                                }}
                                                                            >
                                                                                {selectedOptions[newOption?.id]
                                                                                    ?.quantity || 0}
                                                                            </Typography>
                                                                            <Button
                                                                                onClick={() =>
                                                                                    handleQuantityChange(
                                                                                        newOption?.id,
                                                                                        1,
                                                                                    )
                                                                                }
                                                                                sx={{
                                                                                    minWidth: 32,
                                                                                    height: 32,
                                                                                    borderRadius: 0,
                                                                                    borderLeft: '1px solid #ddd',
                                                                                    color: '#666',
                                                                                }}
                                                                            >
                                                                                +
                                                                            </Button>
                                                                        </Box>
                                                                    ) : (
                                                                        <Typography
                                                                            sx={{ fontSize: 13, color: '#999' }}
                                                                        >
                                                                            {t('product.priceForMembers')}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        );
                                                    })}

                                                {/* 총 금액 */}
                                                {sessionStore.getState().hasSession ? (
                                                    <Box
                                                        sx={{
                                                            mt: 1,
                                                            p: 2,
                                                            bgcolor: '#f8f8f8',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                                            {t('product.totalPriceExcludingVAT')}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                                                            ₩{totalPrice.toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        sx={{
                                                            mt: 1,
                                                            p: 2,
                                                            bgcolor: '#f8f8f8',
                                                            borderRadius: 1,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: 14, color: '#666' }}>
                                                            {t('product.loginToCheckPrice')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                )}
                            </Box>
                        </Box>

                        {isMobile && (
                            <Accordion
                                expanded={expandedAccordions.detail}
                                onChange={handleAccordion('detail')}
                                sx={{
                                    boxShadow: 'none',
                                    mb: 1,
                                    border: 'none',
                                    '&.MuiAccordion-root': {
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                >
                                    {t('product.detailInfo')}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '80px 1fr', md: '120px 1fr' },
                                            rowGap: 1,
                                            columnGap: 2,
                                        }}
                                    >
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                            {t('catalog.manufacturingCountry')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                            {product?.countryOfOrigin
                                                ? translateCountryOfOrigin(product.countryOfOrigin, t)
                                                : '-'}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                            {t('product.color')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>{colorInfo || '-'}</Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>{t('product.size')}</Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>{sizeInfo || '-'}</Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                            {t('product.mixingRatio')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                            {product?.materialInfo && product.materialInfo.length > 0
                                                ? product.materialInfo
                                                      .map((m) => {
                                                          const materialKey = getMaterialKey(m.material);
                                                          const translatedMaterial = t(
                                                              `catalog.materials.${materialKey}`,
                                                          );
                                                          const materialLabel =
                                                              translatedMaterial === `catalog.materials.${materialKey}`
                                                                  ? m.material
                                                                  : translatedMaterial;
                                                          // 도매처문의일 때는 percentage 표시하지 않음
                                                          if (
                                                              materialKey === 'vendor_inquiry' ||
                                                              m.material === '도매처문의'
                                                          ) {
                                                              return materialLabel;
                                                          }
                                                          return `${materialLabel} ${m.percentage}%`;
                                                      })
                                                      .join(', ')
                                                : t('product.contactSeller')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                            {t('product.individualSheet')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                            {(product as any)?.singleUnitAvailable === 't'
                                                ? t('product.available')
                                                : (product as any)?.singleUnitAvailable === 'f'
                                                  ? t('product.unavailable')
                                                  : t('product.contactSeller')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.label}>
                                            {t('product.productRegistrationInfo')}
                                        </Typography>
                                        <Typography sx={DETAIL_TYPOGRAPHY_STYLES.value}>
                                            {product?.createdAt?.split('T')[0]} {t('product.registered')}
                                        </Typography>
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        )}

                        {isMobile && shouldShowCareInfo() && (
                            <>
                                <Divider sx={{ borderColor: '#eee' }} />
                                <Accordion
                                    expanded={expandedAccordions.care}
                                    onChange={handleAccordion('care')}
                                    sx={{
                                        boxShadow: 'none',
                                        mb: 1,
                                        border: 'none',
                                        '&.MuiAccordion-root': {
                                            border: 'none',
                                            '&:before': {
                                                display: 'none',
                                            },
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                    >
                                        {t('product.careInstructions')}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{ mb: 1, fontSize: { xs: 13, md: 15 } }}>
                                            {shouldRenderFabricInfoBox && (
                                                <Box sx={{ mb: hasWashInfo ? 3 : 0 }}>
                                                    <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                        {t('product.fabricInfo')}
                                                    </Typography>
                                                    {renderFabricInfoBox()}
                                                </Box>
                                            )}
                                            {hasWashInfo && (
                                                <>
                                                    <Typography sx={{ ...DETAIL_TYPOGRAPHY_STYLES.label, mb: 2 }}>
                                                        {t('product.washInfo')}
                                                    </Typography>
                                                    {renderWashInfo()}
                                                </>
                                            )}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}

                        {/* 상품 사이즈 아코디언 */}
                        {currentSizeConfig && (
                            <>
                                <Divider sx={{ borderColor: '#eee' }} />
                                <Accordion
                                    expanded={expandedAccordions.size}
                                    onChange={handleAccordion('size')}
                                    sx={{
                                        boxShadow: 'none',
                                        mb: 1,
                                        border: 'none',
                                        '&.MuiAccordion-root': {
                                            border: 'none',
                                            '&:before': {
                                                display: 'none',
                                            },
                                        },
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{ fontWeight: 700, fontSize: { xs: 17, md: 16 } }}
                                    >
                                        {t('product.productSize')}
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {/* 사이즈 가이드 이미지 */}
                                        <Box
                                            sx={{
                                                mb: 3,
                                                width: '100%',
                                                textAlign: 'center',
                                                bgcolor: '#f8f8f8',
                                                borderRadius: 2,
                                                p: 3,
                                                position: 'relative',
                                                '& img': {
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    mixBlendMode: 'multiply',
                                                    filter: 'contrast(1.1)',
                                                },
                                            }}
                                        >
                                            <img
                                                src={getSizeGuideImageUrl(
                                                    currentSizeConfig.imageFileName,
                                                    i18n.language,
                                                )}
                                                alt={`${currentSizeConfig.label} 사이즈 가이드`}
                                                style={{
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    borderRadius: 8,
                                                    display: 'block',
                                                    margin: '0 auto',
                                                }}
                                            />
                                        </Box>

                                        {/* 사이즈 테이블 */}
                                        <Box
                                            sx={{
                                                width: '100%',
                                                overflowX: 'auto',
                                                '&::-webkit-scrollbar': {
                                                    height: 6,
                                                },
                                                '&::-webkit-scrollbar-track': {
                                                    backgroundColor: '#f1f1f1',
                                                    borderRadius: 3,
                                                },
                                                '&::-webkit-scrollbar-thumb': {
                                                    backgroundColor: '#888',
                                                    borderRadius: 3,
                                                    '&:hover': {
                                                        backgroundColor: '#555',
                                                    },
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    minWidth: isMobile ? 'fit-content' : 'auto',
                                                    maxWidth: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                {/* 테이블 헤더 */}
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        borderBottom: '1px solid #eee',
                                                        py: 1.5,
                                                    }}
                                                >
                                                    {/* 사이즈 열의 너비 계산 */}
                                                    {(() => {
                                                        const columnCount = product?.sizeInfo?.[0]
                                                            ? Object.keys(product?.sizeInfo?.[0] || {}).length - 1
                                                            : 0;
                                                        const sizeColumnWidth =
                                                            columnCount <= 4
                                                                ? isMobile
                                                                    ? '12%'
                                                                    : '20%'
                                                                : isMobile
                                                                  ? '12%'
                                                                  : '15%';

                                                        return (
                                                            <Typography
                                                                sx={{
                                                                    width: sizeColumnWidth,
                                                                    minWidth: '60px',
                                                                    fontSize: 14,
                                                                    fontWeight: 700,
                                                                    textAlign: 'center',
                                                                }}
                                                            >
                                                                {t('product.size')}
                                                            </Typography>
                                                        );
                                                    })()}

                                                    {product?.sizeInfo?.[0] &&
                                                        Object.keys(product?.sizeInfo?.[0] || {})
                                                            .filter((key) => key !== 'size')
                                                            .map((field) => {
                                                                const columnCount =
                                                                    Object.keys(product?.sizeInfo?.[0] || {}).length -
                                                                    1;
                                                                // 열 개수에 따라 동적으로 너비 조절
                                                                const getColumnStyle = () => {
                                                                    if (columnCount <= 3) {
                                                                        return {
                                                                            minWidth: '120px',
                                                                            flex: '1 1 25%',
                                                                        };
                                                                    } else if (columnCount <= 5) {
                                                                        return {
                                                                            minWidth: '100px',
                                                                            flex: '1 1 20%',
                                                                        };
                                                                    } else {
                                                                        return {
                                                                            minWidth: '80px',
                                                                            flex: '1 1 15%',
                                                                        };
                                                                    }
                                                                };

                                                                return (
                                                                    <Typography
                                                                        key={field}
                                                                        sx={{
                                                                            ...getColumnStyle(),
                                                                            fontSize: 14,
                                                                            fontWeight: 700,
                                                                            textAlign: 'center',
                                                                        }}
                                                                    >
                                                                        {field}
                                                                    </Typography>
                                                                );
                                                            })}
                                                </Box>

                                                {/* 동적으로 사이즈 행 생성 */}
                                                {product?.sizeInfo?.map((sizeData, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            borderBottom: '1px solid #eee',
                                                            py: 1.5,
                                                            '&:last-child': {
                                                                borderBottom: 'none',
                                                            },
                                                        }}
                                                    >
                                                        {/* 사이즈 열의 너비 계산 */}
                                                        {(() => {
                                                            const columnCount = Object.keys(sizeData).length - 1;
                                                            const sizeColumnWidth =
                                                                columnCount <= 4
                                                                    ? isMobile
                                                                        ? '12%'
                                                                        : '20%'
                                                                    : isMobile
                                                                      ? '12%'
                                                                      : '15%';

                                                            return (
                                                                <Typography
                                                                    sx={{
                                                                        width: sizeColumnWidth,
                                                                        minWidth: '60px',
                                                                        fontSize: 14,
                                                                        fontWeight: 700,
                                                                        textAlign: 'center',
                                                                    }}
                                                                >
                                                                    {sizeData.size}
                                                                </Typography>
                                                            );
                                                        })()}

                                                        {Object.entries(sizeData)
                                                            .filter(([key]) => key !== 'size')
                                                            .map(([key, value]) => {
                                                                const columnCount = Object.keys(sizeData).length - 1;
                                                                // 열 개수에 따라 동적으로 너비 조절
                                                                const getColumnStyle = () => {
                                                                    if (columnCount <= 3) {
                                                                        return {
                                                                            minWidth: '120px',
                                                                            flex: '1 1 25%',
                                                                        };
                                                                    } else if (columnCount <= 5) {
                                                                        return {
                                                                            minWidth: '100px',
                                                                            flex: '1 1 20%',
                                                                        };
                                                                    } else {
                                                                        return {
                                                                            minWidth: '80px',
                                                                            flex: '1 1 15%',
                                                                        };
                                                                    }
                                                                };

                                                                return (
                                                                    <Typography
                                                                        key={key}
                                                                        sx={{
                                                                            ...getColumnStyle(),
                                                                            fontSize: 14,
                                                                            textAlign: 'center',
                                                                        }}
                                                                    >
                                                                        {value}
                                                                    </Typography>
                                                                );
                                                            })}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>

                                        <Typography sx={{ fontSize: 13, color: '#666', mt: 2, textAlign: 'right' }}>
                                            {t('product.sizeMeasurementNote')}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}

                        {/* 제품 설명 아코디언 */}
                        <Accordion
                            expanded={expandedAccordions.desc}
                            onChange={handleAccordion('desc')}
                            sx={{
                                paddingBottom: `${isMobile ? '40px' : '0'}`,
                                boxShadow: 'none',
                                // 요구사항: 제품 설명 섹션의 상단 가로 줄 제거
                                '& .MuiCollapse-root': {
                                    transition: 'height 150ms cubic-bezier(0.4, 0, 0.2, 1) !important',
                                },
                                ...(isMobile && {
                                    '&:not(.Mui-expanded)': {
                                        paddingBottom: '40px',
                                    },
                                }),
                                '& .MuiAccordionSummary-content': {
                                    transition: 'margin 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                                },
                                '& .MuiAccordionSummary-expandIconWrapper': {
                                    transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                                },
                            }}
                            TransitionProps={{
                                timeout: 150,
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: 17, md: 16 },
                                    py: 2,
                                }}
                            >
                                {t('product.productDetail')}
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    pb: 3,
                                    '& .product-description img': {
                                        // 데스크톱: 최대 가로폭 860px 제한, 모바일: 제한 없음
                                        maxWidth: { xs: '100%', md: '860px' },
                                        width: '100%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                        display: 'block',
                                        margin: '0 auto',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease-in-out',
                                        // 요구사항: 둥근 모서리 및 그림자 제거
                                        borderRadius: 0,
                                        boxShadow: 'none',
                                    },
                                    '& .product-description img[src]': {
                                        opacity: 1,
                                    },
                                }}
                            >
                                {shouldRenderDetailThumbnails && isThumbPositionTop && renderDetailThumbnails()}
                                <Box
                                    className="product-description"
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizedDescription,
                                    }}
                                    sx={{
                                        overflow: 'hidden',
                                        wordWrap: 'break-word',
                                        '& p': {
                                            margin: '0 0 16px 0',
                                            padding: 0,
                                            lineHeight: 1.6,
                                        },
                                        '& center': {
                                            display: 'block',
                                            textAlign: 'center',
                                            margin: '16px 0',
                                        },
                                    }}
                                />
                                {shouldRenderDetailThumbnails && !isThumbPositionTop && renderDetailThumbnails()}
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                    {/* 모바일 하단 주문하기 버튼 */}
                    {isMobile && (
                        <Box
                            sx={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 2,
                                bgcolor: '#fff',
                                borderTop: '1px solid #eee',
                                zIndex: theme.zIndex.modal + 1,
                            }}
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e.stopPropagation();
                                e.preventDefault();
                            }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (sessionStore.getState().hasSession) {
                                        handleOptionsDrawerOpen();
                                    } else {
                                        handleLoginClick();
                                    }
                                }}
                                sx={{
                                    height: 48,
                                    bgcolor: sessionStore.getState().hasSession ? '#000' : '#666',
                                    color: '#fff',
                                    fontSize: 16,
                                    fontWeight: 700,
                                    '&:hover': {
                                        bgcolor: sessionStore.getState().hasSession ? '#222' : '#555',
                                    },
                                }}
                            >
                                {sessionStore.getState().hasSession ? t('product.orderNow') : t('product.loginToOrder')}
                            </Button>
                        </Box>
                    )}

                    {/* 스크롤 탑 버튼 */}
                    {showScrollTop && (
                        <Box
                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                e?.preventDefault();
                                e?.stopPropagation();
                                document.getElementById('#modalRef')?.scrollTo?.({ top: 0, behavior: 'smooth' });
                            }}
                            sx={{
                                position: 'sticky',
                                p: { xs: 2.5, md: 3 },
                                bottom: { xs: 35, md: 24 },
                                right: { xs: 16, md: 24 },
                                marginLeft: 'auto',
                                width: { xs: 40, md: 48 },
                                height: { xs: 40, md: 48 },
                                background: 'rgba(255,255,255,0.95)',
                                boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    background: 'rgba(255,255,255,1)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
                                },
                                zIndex: theme.zIndex.modal + 1,
                            }}
                        >
                            <ArrowUpwardIcon
                                sx={{
                                    fontSize: { xs: 24, md: 28 },
                                    color: '#222',
                                }}
                            />
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* 모바일 상품 옵션 바텀 시트 */}
            <Drawer
                anchor="bottom"
                open={isOptionsDrawerOpen}
                onClose={handleOptionsDrawerClose}
                PaperProps={{
                    sx: {
                        maxHeight: '85vh',
                        borderTopLeftRadius: 20,
                        borderTopRightRadius: 20,
                        zIndex: theme.zIndex.modal + 2,
                    },
                }}
                transitionDuration={300}
                sx={{
                    '& .MuiDrawer-paper': {
                        background: '#fff',
                    },
                    '& .MuiBackdrop-root': {
                        backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    },
                    zIndex: theme.zIndex.modal + 2,
                }}
            >
                <Box sx={{ p: 2 }}>
                    {/* 바텀 시트 헤더 */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{t('product.productOptions')}</Typography>
                        <IconButton onClick={handleOptionsDrawerClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* 옵션 선택 영역 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {product?.matchingProducts
                            ?.filter((p) => p?.isActivated !== 'f' && p?.isPublic !== 'f')
                            .map((option, index) => {
                                const newOption = extractOptionColor(option, product.colorNameInfo);
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            pb: 2,
                                            borderBottom: '1px solid #eee',
                                            '&:last-child': {
                                                borderBottom: 'none',
                                                pb: 0,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {(newOption?.optionImageUrl || newOption?.color) && (
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        border: '1px solid #eee',
                                                    }}
                                                >
                                                    {newOption?.optionImageUrl ? (
                                                        <img
                                                            src={newOption?.optionImageUrl}
                                                            alt={
                                                                getLocalizedNameFromI18n(
                                                                    {
                                                                        name: newOption?.optionName,
                                                                        nameI18n: (option as any).optionNameI18n,
                                                                    },
                                                                    i18n.language,
                                                                ) || newOption?.optionName
                                                            }
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Box
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                bgcolor: newOption?.color,
                                                                border: '1px solid #eee',
                                                                borderRadius: 1,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        ...(!newOption?.optionImageUrl && !newOption?.color
                                                            ? {
                                                                  display: 'inline-block',
                                                                  mr: 1,
                                                              }
                                                            : {
                                                                  mb: 0.2,
                                                              }),
                                                    }}
                                                >
                                                    {getLocalizedNameFromI18n(
                                                        {
                                                            name: newOption?.optionName,
                                                            nameI18n: (option as any).optionNameI18n,
                                                        },
                                                        i18n.language,
                                                    ) || newOption?.optionName}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                        ...(!newOption?.optionImageUrl && !newOption?.color
                                                            ? {
                                                                  display: 'inline-block',
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    {sessionStore.getState().hasSession ? (
                                                        <>₩{newOption?.supplyPrice?.toLocaleString()}</>
                                                    ) : (
                                                        <>{t('product.priceForMembers')}</>
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {sessionStore.getState().hasSession ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        border: '1px solid #ddd',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    <Button
                                                        onClick={() => handleQuantityChange(newOption?.id, -1)}
                                                        sx={{
                                                            minWidth: 32,
                                                            height: 32,
                                                            borderRadius: 0,
                                                            borderRight: '1px solid #ddd',
                                                            color: '#666',
                                                        }}
                                                    >
                                                        -
                                                    </Button>
                                                    <Typography
                                                        sx={{
                                                            width: 40,
                                                            textAlign: 'center',
                                                            fontSize: 13,
                                                        }}
                                                    >
                                                        {selectedOptions[newOption?.id]?.quantity || 0}
                                                    </Typography>
                                                    <Button
                                                        onClick={() => handleQuantityChange(newOption?.id, 1)}
                                                        sx={{
                                                            minWidth: 32,
                                                            height: 32,
                                                            borderRadius: 0,
                                                            borderLeft: '1px solid #ddd',
                                                            color: '#666',
                                                        }}
                                                    >
                                                        +
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: 13, color: '#999' }}>
                                                    {t('product.priceForMembers')}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}

                        {/* 총 금액 */}
                        {sessionStore.getState().hasSession ? (
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 2,
                                    bgcolor: '#f8f8f8',
                                    borderRadius: 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                                    {t('product.totalPriceExcludingVAT')}
                                </Typography>
                                <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                                    ₩{totalPrice.toLocaleString()}
                                </Typography>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 2,
                                    bgcolor: '#f8f8f8',
                                    borderRadius: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography sx={{ fontSize: 14, color: '#666' }}>
                                    {t('product.loginToCheckPrice')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </>
    );
};

export default ProductModal;
